'use client'

import { CartItemList } from '@/feature/cart/components/cart-item-list/CartItemList'
import { getEntryOpensInNewTab } from '@/feature/header/services/header.utils'
import { toOurProductsPage } from '@/feature/product/services/product.routing'
import { twMerge } from '@/lib/classname/twMerge'
import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { ensureLeadingSlash } from '@/lib/utils/link-utils'
import { Accordion } from '@/ui/accordion/Accordion'
import { AccordionItem } from '@/ui/accordion/AccordionItem'
import { Button } from '@/ui/buttons/button/Button.client'
import { CloseButton } from '@/ui/buttons/close-button/CloseButton.client'
import { Icon } from '@/ui/icons/icon/Icon'
import { InlineImageLink } from '@/ui/links/inline-image-link/InlineImageLink.client'
import { Link } from '@/ui/links/link/Link.client'
import { Picture } from '@/ui/media/picture/Picture.client'
import type { MenuEntryFragment } from '@gql-generated/graphql'
import { cva } from 'class-variance-authority'
import { atom, useAtom } from 'jotai'
import { useCallback, useMemo, useState } from 'react'
import { Dialog, Modal } from 'react-aria-components'
import { toMenuEntryPage } from '../../services/navigation.routing'

// #region Types ------------------------------

export type MobileMenuProps = {
  /** The menu entries to display on the menu */
  menuEntries: MenuEntryFragment[] | null | undefined
}

export type MobileMenuListProps = {
  /** The categories to display on the menu */
  entries: MenuEntryFragment['entries']

  /** When a submenu is selected */
  onSelectedSubMenu?: (urlKey: string | null) => void

  /** The action that close the menu */
  closeAction?: () => void

  /** Is this menu only displaying links instead of menu categories */
  linksOnly?: boolean
}

// #endregion

// #region MobileMenuList ---------------------

function MobileMenuList({
  entries,
  onSelectedSubMenu,
  closeAction,
  linksOnly,
}: MobileMenuListProps): React.ReactNode {
  if (!entries) {
    return null
  }

  return (
    <CartItemList withDividers>
      {entries.map(({ name, seoContent, image, ...optionalProps }) => {
        const hasSubEntries = optionalProps.entries && optionalProps.entries?.length > 0
        const isRealLink =
          !hasSubEntries && (optionalProps.__typename === 'MenuEntryLink' || linksOnly)

        return (
          <CartItemList.Item key={`${name}-${seoContent}`}>
            <InlineImageLink
              href={isRealLink ? toMenuEntryPage(optionalProps) : undefined}
              opensInNewTab={getEntryOpensInNewTab(optionalProps)}
              onPress={() => {
                if (onSelectedSubMenu && hasSubEntries) {
                  onSelectedSubMenu(`${name}-${seoContent}`)
                } else if (optionalProps.__typename === 'MenuEntryLink' || linksOnly) {
                  closeAction?.()
                }
              }}
              title={name ?? ''}
              containerClassName="min-w-[20rem]"
              image={image ?? undefined}
              overflow
              trackingData={{
                event: 'gaEvent',
                eventCategory: 'homepage',
                eventAction: 'mobile - nos univers',
                eventLabel: `clic univers | ${name}`,
              }}
            >
              {name}
            </InlineImageLink>
          </CartItemList.Item>
        )
      })}
    </CartItemList>
  )
}

// #endregion

// #region Atoms & Hooks ----------------------

const isMobileMenuOpenAtom = atom(false)

export const openMobileMenuSetAtom = atom(null, (_get, set) => set(isMobileMenuOpenAtom, true))
export const closeMobileMenuSetAtom = atom(null, (_get, set) => set(isMobileMenuOpenAtom, false))

// #endregion Atoms & Hooks

// #region MobileMenu -------------------------

const mobileMenuCva = cva('flex max-h-screen flex-col bg-white', {
  variants: {
    subMenu: {
      true: 'absolute top-0 size-full',
      false: 'hidden',
    },
  },
})

export function MobileMenu({ menuEntries }: MobileMenuProps): React.ReactElement {
  const { t } = useTranslation(['mobileMenu'])
  const [selectedSubMenu, setSelectedSubMenu] = useState<string | null>(null)
  const selectedSubCat = useMemo(() => {
    if (selectedSubMenu === null) {
      return undefined
    }

    const allSubCategories: MenuEntryFragment[] = []

    menuEntries
      ?.filter((subEntries) => !!subEntries)
      .forEach(({ entries: subEntries }) => {
        if (subEntries) {
          allSubCategories.push(...subEntries)
        }
      })

    return allSubCategories.find(
      (subCategory) => `${subCategory.name}-${subCategory.seoContent}` === selectedSubMenu,
    )
  }, [selectedSubMenu, menuEntries])

  const isProduct = useMemo(() => {
    return (
      selectedSubCat?.__typename === 'MenuEntryCategory' &&
      ensureLeadingSlash(selectedSubCat?.slug).startsWith(toOurProductsPage())
    )
  }, [selectedSubCat])

  const isSubMenu = selectedSubCat !== undefined

  const contentMenuList = useCallback(
    (toDisplaySubCat: boolean, close: () => void): React.ReactNode => {
      if (toDisplaySubCat && selectedSubCat?.entries) {
        const clonedEntries = [...selectedSubCat.entries]

        // Remove the first entry which is used as title for the submenu,
        // but only for non-product menus.
        if (!isProduct) {
          clonedEntries.shift()
        }

        return (
          <div className="overflow-y-auto px-2">
            <MobileMenuList entries={clonedEntries} linksOnly closeAction={close} />
          </div>
        )
      }

      if (!menuEntries?.length) {
        return null
      }

      return (
        <div className="px-2 pb-2">
          <Accordion opensFirst>
            {menuEntries.map(({ name, seoContent, entries: subEntries }) => (
              <AccordionItem id={name ?? ''} key={`${name}-${seoContent}`}>
                <AccordionItem.Header title={name ?? ''} />
                <AccordionItem.Panel className="px-0">
                  {!!subEntries?.length ? (
                    <MobileMenuList
                      entries={subEntries}
                      onSelectedSubMenu={(sentKey) => setSelectedSubMenu(sentKey)}
                      closeAction={close}
                    />
                  ) : null}
                </AccordionItem.Panel>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )
    },
    [isProduct, menuEntries, selectedSubCat],
  )

  const [isOpen, toggle] = useAtom(isMobileMenuOpenAtom)

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={toggle}
      className="fixed top-0 z-mobile-menu block size-full desktop:hidden"
    >
      <Dialog className="h-full" aria-label={t('mobileMenu:ariaLabel')}>
        {({ close }) => (
          <div data-testid="mobile-menu" className="size-full">
            <div className="flex h-full flex-col overflow-y-auto bg-white">
              <div className="bg-white px-2 pt-2">
                <div className="mb-4 flex flex-row justify-end">
                  <CloseButton onPress={close} />
                </div>
              </div>
              {contentMenuList(false, close)}
            </div>
            {!!selectedSubCat && (
              <div
                data-testid="mobile-menu"
                className={twMerge('', mobileMenuCva({ subMenu: isSubMenu }))}
              >
                <div className="bg-brand-2-50 px-2 pb-4 pt-2">
                  <div className="mb-4 flex flex-row justify-between">
                    <Button
                      size="sm"
                      onPress={() => setSelectedSubMenu(null)}
                      variant="tertiary"
                      isRound
                      iconLeft={<Icon.ArrowLeft />}
                      aria-label={t('mobileMenu:back')}
                    />
                    <CloseButton onPress={close} />
                  </div>
                  <div className="group/category-row flex w-full items-center gap-2 self-stretch leading-light">
                    {!!selectedSubCat.image && (
                      <div className="flex size-5 shrink-0 items-center justify-center">
                        <Picture
                          className="h-full w-5"
                          image={selectedSubCat.image}
                          mode="contain"
                        />
                      </div>
                    )}
                    <div className="flex flex-col">
                      <span className="text-lg font-bold text-brand-1-700">
                        {selectedSubCat.name}
                      </span>
                      <Link
                        variant="free"
                        href={toMenuEntryPage(selectedSubCat)}
                        opensInNewTab={getEntryOpensInNewTab(selectedSubCat)}
                        title={selectedSubCat.name ?? ''}
                        bold
                        underlined="invert"
                        onPress={close}
                      >
                        {isProduct
                          ? t('mobileMenu:menuPopinParentCategory')
                          : selectedSubCat?.entries?.[0]?.name}
                      </Link>
                    </div>
                  </div>
                </div>
                {contentMenuList(true, close)}
              </div>
            )}
          </div>
        )}
      </Dialog>
    </Modal>
  )
}

// #endregion
